import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { SellerHeader } from '../components/SellerHeader';
import { BottomNavigation } from '../components/BottomNavigation';
import { ProductCard } from '../components/ProductCard';
import { ProductFilters } from '../components/ProductFilters';
import { Pagination } from '../components/Pagination';
import { EmptyState } from '../components/EmptyState';
import { useProducts } from '../hooks/useProducts';
import { archiveProduct } from '../utils/productApi';
import { COLORS } from '../lib/styleGuide';

// Style constants
const FONTS = {
  heading: { fontFamily: 'Inter, system-ui, sans-serif' },
  body: { fontFamily: 'Inter, system-ui, sans-serif' }
};

export default function AllProducts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedCardId, setExpandedCardId] = useState(null);

  // Use the custom hook for product management
  const {
    products: hookProducts,
    totalProducts,
    loading,
    error,
    filters,
    sortBy,
    currentPage,
    totalPages,
    availableCategories,
    availableSubCategories,
    taxonomyData,
    setSortBy,
    setCurrentPage,
    updateFilters,
    clearFilters,
    refreshProducts,
    startIndex,
    endIndex
  } = useProducts();

  // Local state for products to handle updates
  const [products, setProducts] = useState(hookProducts);

  // Read URL parameters and apply to filters
  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam && statusParam !== filters.status) {
      updateFilters({ status: statusParam });
    }
  }, [searchParams, filters.status, updateFilters]);

  // Update local products when hook products change
  useEffect(() => {
    setProducts(hookProducts);
  }, [hookProducts]);

  // Handle product archival
  const handleDeleteProduct = useCallback(async (productId) => {
    try {
      console.log(`🔍 [AllProducts] Product ${productId} archived by ProductCard, refreshing list...`);
      
      // Wait a moment to ensure the database operations complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh the products list after archival
      refreshProducts();
    } catch (error) {
      console.error('Error refreshing products after archival:', error);
    }
  }, [refreshProducts]);

  // Handle card expansion
  const handleCardExpand = useCallback((productId) => {
    setExpandedCardId(expandedCardId === productId ? null : productId);
  }, [expandedCardId]);

  // Handle add product navigation
  const handleAddProduct = useCallback(() => {
    navigate('/add-product');
  }, [navigate]);

  // Calculate active filter count
  const activeFilterCount = Object.values(filters).filter(value => 
    value !== 'all' && value !== ''
  ).length;

  // Handle error state
  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: COLORS.NEUTRAL_BG, paddingBottom: '80px' }}>
        <SellerHeader />
        <div style={{ paddingTop: '72px', padding: '16px' }}>
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '24px',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: 18,
              fontWeight: 600,
              color: COLORS.NEUTRAL_TEXT,
              margin: '0 0 8px 0',
              ...FONTS.heading
            }}>
              Error Loading Products
            </h3>
            <p style={{
              fontSize: 14,
              color: COLORS.ACCENT_GRAY,
              margin: '0 0 16px 0',
              ...FONTS.body
            }}>
              {error}
            </p>
            <button
              onClick={refreshProducts}
              style={{
                background: COLORS.PRIMARY_BROWN,
                color: COLORS.NEUTRAL_WHITE,
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                ...FONTS.body
              }}
            >
              Try Again
            </button>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: COLORS.NEUTRAL_BG, paddingBottom: '80px' }}>
      <SellerHeader />

      {/* Main Content */}
      <div style={{ paddingTop: '72px' }}>
        {/* Header with Add Product Button */}
        <div style={{ 
          background: COLORS.NEUTRAL_WHITE, 
          padding: '12px 16px',
          borderBottom: '1px solid #E0E0E0'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center', 
            marginBottom: 16 
          }}>
            {/* Add Product Button */}
            <button
              onClick={handleAddProduct}
              style={{
                background: COLORS.PRIMARY_GRADIENT,
                color: COLORS.NEUTRAL_WHITE,
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                ...FONTS.body
              }}
              aria-label="Add new product"
            >
              <Plus size={18} />
              Add Product
            </button>
          </div>
        </div>
        
        {/* Filters */}
        <ProductFilters
          filters={filters}
          onUpdateFilters={updateFilters}
          onClearFilters={clearFilters}
          availableCategories={availableCategories}
          availableSubCategories={availableSubCategories}
          activeFilterCount={activeFilterCount}
          taxonomyData={taxonomyData}
        />

        {/* Products Content */}
        <div style={{ padding: '16px' }}>
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200
            }}>
              <div style={{ 
                fontSize: 14, 
                color: COLORS.ACCENT_GRAY,
                ...FONTS.body
              }}>
                Loading products...
              </div>
            </div>
          ) : products.length === 0 ? (
            <EmptyState
              hasFilters={activeFilterCount > 0}
              hasSearch={!!filters.search}
              onAddProduct={handleAddProduct}
              onClearFilters={clearFilters}
            />
          ) : (
            <div>
              {/* Products List */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 16,
                marginBottom: 24
              }}>
                {products.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onDelete={handleDeleteProduct}
                    isExpanded={expandedCardId === product.id}
                    onToggleExpand={() => handleCardExpand(product.id)}
                    onUpdate={(productId, updatedData) => {
                      // Update the product in the local state
                      setProducts(prevProducts => 
                        prevProducts.map(p => 
                          p.id === productId ? { ...p, ...updatedData } : p
                        )
                      );
                      // Product updated successfully
                    }}
                  />
                ))}
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                startIndex={startIndex}
                endIndex={endIndex}
                totalItems={totalProducts}
              />
            </div>
          )}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
}
